/* eslint-disable max-lines */
import React, { useCallback, useState, useContext, useMemo } from "react";
import { Form, Formik, FormikConfig, useField } from "formik";
import {
  Alert,
  RadioGroup, FormControl, FormLabel, FormControlLabel, Radio,
  FormHelperText, InputBaseComponentProps,
  Typography, InputAdornment, IconButton, Grid,
} from "@mui/material";
import * as yup from "yup";
import { useMutation, type MutationResult } from "@apollo/client";
import classNames from "classnames";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Card, Button } from "components/haven-design";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { TosCheckbox } from "components/haven-design/form/checkbox";
import { CreateApplicantAccount, CreateApplicantAccountVariables, UserLoginPreflight, UserLoginPreflightVariables, UserRole } from "types/gql-types";
import { initializeClientSignup } from "services/analytics";
import { apolloClient } from "data/graphql";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import { debounceLatest, identifierIsAvailable } from "../identifier-available";
import { CREATE_NEW_USER, SIGN_IN_ATTEMPT } from "../query";
import { Page } from "../type";
import { passwordValidations } from "../utils";
import LoginModalContext from "../context/modal";
import styles from "./Login.module.css";
import { LoginCardSubtitle, LoginCardTitle, LoneGrid, TextMaskCustom } from "./shared-components";
import { loginStyles, loginAuthColumnSx } from "./styles";

const validateIdentifierAvailable = async (identifier: string | undefined) => {
  if (!identifier) return true;

  try {
    const result = await apolloClient.mutate<UserLoginPreflight, UserLoginPreflightVariables>({
      mutation: SIGN_IN_ATTEMPT,
      variables: { username: identifier },
    });
    return identifierIsAvailable(result.data?.userLoginPreflight);
  } catch {
    return true;
  }
};

type FormValues = {
  firstName: string;
  lastName: string;
  loginMethod: "email" | "phone";
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  tos: boolean;
}

const INITIAL_FORM_VALUES: FormValues = {
  firstName: "",
  lastName: "",
  loginMethod: "email",
  email: "",
  phone: "",
  password: "",
  passwordConfirm: "",
  tos: false,
};

const getSignUpValidationSchema = (
  debouncedValidateIdentifier: (id: string | undefined) => Promise<boolean>,
  debouncedValidateIdentifierPhone: (id: string | undefined) => Promise<boolean>,
) =>
  yup.object({
    firstName: yup.string()
      .matches(/^\s*[A-z'-\s]+\d{0}\s*$/, "First Name can only include letters, apostrophes or dashes")
      .required(),
    lastName: yup.string()
      .matches(/^\s*[A-z'-\s]+\d{0}\s*$/, "Last Name can only include letters, apostrophes or dashes")
      .required(),
    loginMethod: yup.string().oneOf(["email", "phone"]),
    email: yup.string().when("loginMethod", {
      is: "email",
      then: (schema) => schema.required("A valid email address is required."),
      otherwise: (schema) => schema,
    }).email()
      .test("emailAvailable", "Account already exists with this email", (value, context) => {
        if (!context.parent.email || !context.parent.email.trim()) return true;
        return debouncedValidateIdentifier(value);
      }),
    phone: yup.string().when("loginMethod", {
      is: "phone",
      then: (schema) => schema.required("A valid US mobile phone number is required"),
      otherwise: (schema) => schema,
    }).validPhoneNumber()
      .test("phoneAvailable", "Account already exists with this mobile phone", (value, context) => {
        if (!context.parent.phone || !context.parent.phone.trim()) return true;
        return debouncedValidateIdentifierPhone(value);
      }),
    password: yup
      .mixed()
      .when("loginMethod", {
        is: "email",
        then: () => yup.string().validPassword().required(),
        otherwise: (schema) => schema,
      }),
    tos: yup
      .bool()
      .oneOf(
        [true],
        "You must agree to the Terms of Use and Privacy Policy in order to create an account.",
      ),
  });

type LoginMethodSelectorProps = { name: string };
const LoginMethodSelector: React.FC<LoginMethodSelectorProps> = (props) => {
  const { name, ...radioGroupProps } = props;
  const [field, meta] = useField(name);

  return (
    <FormControl>
      <FormLabel>
        How do you want to login?
      </FormLabel>
      <RadioGroup
        row
        {...field}
        {...radioGroupProps}
      >
        <FormControlLabel
          value="email"
          label="Email"
          control={<Radio />}
        />
        <FormControlLabel
          value="phone"
          label="Mobile Phone"
          control={<Radio />}
        />
        { meta.touched && meta.error && (<FormHelperText>{meta.error}</FormHelperText>)}
      </RadioGroup>
    </FormControl>
  );
};

type SignUpPageProps = React.ComponentProps<Page<"create">>;

const useSignUpSubmit = (props: SignUpPageProps) => {
  const [createNewUser, createResult] = useMutation<
  CreateApplicantAccount,
  CreateApplicantAccountVariables
  >(CREATE_NEW_USER);
  const { openSnackbar } = useSnackbar();
  const onSubmit: FormikConfig<FormValues>["onSubmit"] = useCallback((values: FormValues) => {
    const variables = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email,
      phone: values.phone,
      password: values.password,
      roles: [UserRole.CLIENT],
    };
    createNewUser({ variables: { data: variables } }).then((result) => {
      const id = result.data?.createUser?.id;
      if (!id) return;
      initializeClientSignup({
        userId: id,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
      });
      openSnackbar({ message: "Account successfully created", status: "success" });
      switch (values.loginMethod) {
        case "email":
          props.onNavigate({
            page: "wait-to-verify",
            props: { username: values.email, formId: props.formId },
          });
          break;
        case "phone":
          props.onNavigate({ page: "login", props: {} });
          break;
        default:
          break;
      }
    }).catch((err) => {
      logger.sendError(err);
      openSnackbar({ message: "Account could not be created", status: "error" });
    });
  }, [createNewUser, openSnackbar, props]);
  return { createResult, onSubmit };
};

const useSignUpFormState = () => {
  const [pass, setPass] = useState<string>("");
  const [passwordErrors, setPasswordErrors] =
    useState(() => passwordValidations.filter(({ check }) => check("")));
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const handleTogglePasswordVisibility = useCallback(
    () => setShowPassword((prev) => !prev),
    [],
  );
  const validatePassword = useCallback((password: string) => {
    setPasswordErrors(passwordValidations.filter(({ check }) => check(password)));
  }, []);
  const renderPasswordValidations = useCallback(() => {
    const errorsList = passwordErrors.map((error, idx) => <li key={idx}>{error.shortMessage}</li>);
    if (errorsList.length < 1) return <></>;
    return (
      <Grid
        size={{ xs: 12 }}
        sx={loginAuthColumnSx}
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <ul style={loginStyles.passwordRequirements}>{errorsList}</ul>
      </Grid>
    );
  }, [passwordErrors]);
  return {
    pass,
    setPass,
    showPassword,
    handleTogglePasswordVisibility,
    validatePassword,
    renderPasswordValidations,
  };
};

const SignUpFormHeader: React.FC = () => (
  <Grid>
    <LoginCardTitle title="Find Affordable Housing" />
    <LoginCardSubtitle>
      Join Haven Connect to apply to affordable housing opportunities and waitlists.
    </LoginCardSubtitle>
  </Grid>
);

const SignUpFormInputs: React.FC = () => (
  <>
    <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
      <InputWithErrorLabel
        label="First name"
        name="firstName"
        type="text"
        style={loginStyles.loginInput}
        autoFocus
      />
    </Grid>
    <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
      <InputWithErrorLabel
        label="Last name"
        name="lastName"
        type="text"
        style={loginStyles.loginInput}
      />
    </Grid>
    <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
      <InputWithErrorLabel
        label="Email address"
        name="email"
        type="text"
        style={loginStyles.loginInput}
      />
    </Grid>
    <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
      <InputWithErrorLabel
        label="Mobile Phone"
        name="phone"
        type="text"
        InputProps={{
          inputComponent: TextMaskCustom as React.ElementType<InputBaseComponentProps>,
        }}
        style={loginStyles.loginInput}
        placeholder="(999) 123-4567"
      />
    </Grid>
  </>
);

type SignUpPasswordSectionProps = {
  form: { setFieldValue: (n: string, v: string, b?: boolean) => void };
  showPassword: boolean;
  onToggle: () => void;
  pass: string;
  onPasswordChange: (value: string) => void;
  renderValidations: () => React.ReactNode;
};

const SignUpPasswordSection: React.FC<SignUpPasswordSectionProps> = ({
  form, showPassword, onToggle, pass, onPasswordChange, renderValidations,
}) => (
  <>
    <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
      <InputWithErrorLabel
        label="Choose a password"
        name="password"
        type={showPassword ? "text" : "password"}
        style={loginStyles.loginInput}
        value={pass}
        onChange={(e) => {
          form.setFieldValue("password", e.target.value, true);
          onPasswordChange(e.target.value);
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={onToggle}
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Grid>
    {renderValidations()}
  </>
);

type SignUpFormContentProps = {
  form: {
    values: FormValues;
    touched: object;
    isValid: boolean;
    setFieldValue: (name: string, value: string, validate?: boolean) => void;
  };
  createResult: MutationResult<CreateApplicantAccount>;
  inModal: boolean;
  children?: React.ReactNode;
  pass: string;
  setPass: (value: string) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  validatePassword: (value: string) => void;
  renderPasswordValidations: () => React.ReactNode;
};

const SignUpFormFooter: React.FC<{
  form: SignUpFormContentProps["form"];
  createResult: SignUpFormContentProps["createResult"];
  children?: React.ReactNode;
}> = ({ form, createResult, children }) => (
  <Grid
    container
    spacing={2}
    direction="column"
    alignItems="stretch"
    sx={{ width: "100%" }}
  >
    <Grid size={{ xs: 12 }}>
      <Alert icon={false} severity="info">
        <Typography variant="caption">
          By proceeding, you are consenting to receive calls or SMS messages including
          by automated dialer, from Haven Connect and its affiliates to the number
          you provide. You understand that you may opt out by texting &quot;STOP&quot; to
          the Haven Connect number that messaged you.
        </Typography>
      </Alert>
    </Grid>
    <Grid size={{ xs: 12 }}>
      <Button
        id="create-account"
        type="submit"
        color="primary"
        size="L"
        fullWidth
        className={styles.loginButton}
        disabled={
          Object.keys(form.touched).length === 0 ||
          !form.isValid ||
          createResult.loading
        }
      >
        Create Your New Account
      </Button>
      {children}
    </Grid>
    <Grid size={{ xs: 12 }}>
      <LoneGrid>
        {!createResult.loading && createResult.error && (
          <FormHelperText error style={{ marginTop: 24 }}>
            {createResult.error.graphQLErrors[0].message}
          </FormHelperText>
        )}
      </LoneGrid>
    </Grid>
  </Grid>
);

const SignUpFormContent: React.FC<SignUpFormContentProps> = ({
  form, createResult, inModal, children,
  pass, setPass, showPassword, onTogglePassword, validatePassword, renderPasswordValidations,
}) => {
  const handlePasswordChange = useCallback(
    (value: string) => {
      setPass(value);
      validatePassword(value);
    },
    [setPass, validatePassword],
  );
  return (
    <Form className={styles.form}>
      <Card className={classNames(styles.loginCard, { [styles.borderlessCard]: inModal })}>
        <Grid
          container
          spacing={1}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <SignUpFormHeader />
          <SignUpFormInputs />
          {form.values.loginMethod === "email" && (
            <SignUpPasswordSection
              form={form}
              showPassword={showPassword}
              onToggle={onTogglePassword}
              pass={pass}
              onPasswordChange={handlePasswordChange}
              renderValidations={renderPasswordValidations}
            />
          )}
          <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}><LoginMethodSelector name="loginMethod" /></Grid>
          <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}><TosCheckbox name="tos" /></Grid>
          <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
            <SignUpFormFooter form={form} createResult={createResult}>
              {children}
            </SignUpFormFooter>
          </Grid>
        </Grid>
      </Card>
    </Form>
  );
};

const SignUpCard: Page<"create"> = (props) => {
  const { inModal } = useContext(LoginModalContext);
  const formState = useSignUpFormState();
  const { createResult, onSubmit } = useSignUpSubmit(props);
  const debouncedValidateIdentifier = useMemo(
    () => debounceLatest(validateIdentifierAvailable, 500),
    [],
  );
  const debouncedValidateIdentifierPhone = useMemo(
    () => debounceLatest(validateIdentifierAvailable, 500),
    [],
  );
  const validationSchema = useMemo(
    () => getSignUpValidationSchema(debouncedValidateIdentifier, debouncedValidateIdentifierPhone),
    [debouncedValidateIdentifier, debouncedValidateIdentifierPhone],
  );
  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={INITIAL_FORM_VALUES}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(form) => (
        <SignUpFormContent
          form={form}
          createResult={createResult}
          inModal={inModal}
          pass={formState.pass}
          setPass={formState.setPass}
          showPassword={formState.showPassword}
          onTogglePassword={formState.handleTogglePasswordVisibility}
          validatePassword={formState.validatePassword}
          renderPasswordValidations={formState.renderPasswordValidations}
        >
          {props.children}
        </SignUpFormContent>
      )}
    </Formik>
  );
};

export default SignUpCard;
